import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BaseDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  preventBackdropClose?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-7xl',
};

/**
 * Shared modal shell. Built on the native <dialog> element so we get
 * focus trap + Escape handling for free.
 */
export function BaseDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
  preventBackdropClose,
}: BaseDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    el.addEventListener('cancel', handleCancel);
    return () => el.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      onClick={(e) => {
        if (preventBackdropClose) return;
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'rounded-xl border border-neutral-200 bg-neutral-0 p-0 shadow-xl backdrop:bg-neutral-900/50',
        'w-full',
        sizeClasses[size],
        className,
      )}
      aria-labelledby="dialog-title"
      aria-describedby={description ? 'dialog-desc' : undefined}
    >
      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-16 border-b border-neutral-200 p-20">
          <div>
            <h2 id="dialog-title" className="text-xl font-semibold text-neutral-900">
              {title}
            </h2>
            {description && (
              <p id="dialog-desc" className="mt-4 text-sm text-neutral-600">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm p-4 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
            aria-label="Close dialog"
          >
            <X className="h-20 w-20" />
          </button>
        </div>
        <div className="p-20">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-12 border-t border-neutral-200 bg-neutral-50 p-16">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
